import { useSelector } from "react-redux";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getUserDisplayName, getUserInitials } from "@/lib/userDisplay";
import { selectCurrentUser } from "@/features/auth/authSlice";
import { getProfilePictureUrl, getUserDetailsUrl } from "@/features/auth/authService";
import { cn } from "@/lib/utils";

/**
 * The operator's identity in the header, and a way through to their profile.
 *
 * This is a link, not a menu. The old app made both the avatar and the name a
 * link straight to the user-details page on the auth host, and the redesign
 * turned that into a dropdown whose every item now exists as its own header
 * control — Theme, Settings and Sign out each have a button on the bar — so the
 * menu had one unique entry, "View profile", buried one tap behind the thing
 * people were already clicking to get there.
 *
 * Opens in a new tab, as it did before: the profile is a different application,
 * and an operator part-way through a queue should not lose it to a navigation.
 */
export function UserProfileLink({ className = "" }) {
  const user = useSelector(selectCurrentUser);

  const name = getUserDisplayName(user) || "Signed in";
  const email = user?.email ?? "";
  const role = user?.roles?.[0]?.description ?? "";
  const pictureUrl = getProfilePictureUrl(user?.guid);
  const profileUrl = getUserDetailsUrl(user);

  const identity = (
    <>
      <Avatar size="default" className="size-8 shrink-0">
        {/* Falls through to initials on 404 or a cross-origin failure, which is
            what happens on localhost where the cookie is not sent. */}
        {pictureUrl && <AvatarImage src={pictureUrl} alt="" />}
        <AvatarFallback className="text-xs font-semibold text-foreground">
          {getUserInitials(user)}
        </AvatarFallback>
      </Avatar>
      <span className="page-header-account-text hidden min-w-0 sm:block">
        <span className="block truncate text-sm font-medium leading-none text-foreground">
          {name}
        </span>
        {(role || email) && (
          <span className="mt-1 block truncate text-xs leading-none text-muted-foreground">
            {role || email}
          </span>
        )}
      </span>
    </>
  );

  const shared = cn(
    "page-header-account touch-target flex min-w-0 items-center gap-2 rounded-[0.8rem] p-1 text-left outline-none transition-colors",
    className,
  );

  // Without a guid there is no profile to open, so it stays a plain identity
  // block rather than a link that goes nowhere.
  if (!profileUrl) {
    return <span className={shared}>{identity}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open the profile for ${name} in a new tab`}
            className={cn(shared, "hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring")}
          />
        }
      >
        {identity}
      </TooltipTrigger>
      <TooltipContent>Open your profile</TooltipContent>
    </Tooltip>
  );
}
